import { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import {
  CloseButton,
  Button,
  Input,
  Textarea,
  NativeSelect,
  VStack,
  HStack,
  Box,
  Badge,
  Text,
  IconButton,
  Switch,
  Separator,
  Stack,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { extractApiMessage } from '../../lib/apiError'
import { useFollowUpKinds } from '../../hooks/useFollowUp'
import { FOLLOW_UP_KIND_LABELS, type FollowUpKind } from '../../types/followUp'
import { FiPlus, FiTrash2, FiMove } from 'react-icons/fi'
import { useCreateSurveyQuestion, useUpdateSurveyQuestion, useSurveyGroups } from '../../hooks/useSurvey'
import type { SaveSurveyQuestionRequest, SurveyQuestionDetailDto } from '../../types/survey'
import { SurveyType, AppliesTo, AccessLevel, SURVEY_TYPE_LABELS, APPLIES_TO_LABELS, ACCESS_LEVEL_LABELS } from '../../types/survey'

interface SurveyQuestionFormModalProps {
  isOpen: boolean
  onClose: () => void
  question?: SurveyQuestionDetailDto | null
}

export function SurveyQuestionFormModal({ isOpen, onClose, question }: SurveyQuestionFormModalProps) {
  const isEdit = !!question
  const { data: groups } = useSurveyGroups()
  const { data: followUpKinds } = useFollowUpKinds()
  const activeCustomKinds = (followUpKinds ?? []).filter(k => k.isActive)

  const { register, handleSubmit, reset, control, watch, setValue } = useForm<SaveSurveyQuestionRequest>({
    defaultValues: {
      label: '',
      labelSpanish: '',
      question: '',
      questionSpanish: '',
      type: SurveyType.Volunteer,
      cycle: new Date().getFullYear(),
      appliesTo: AppliesTo.People,
      surveyGroupId: null,
      isActive: true,
      accessLevel: AccessLevel.AllCommittees,
      answers: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'answers',
  })

  const isActive = watch('isActive')
  const watchedAnswers = watch('answers')

  const followUpValue = (a?: { followUpKind?: FollowUpKind | null; followUpCustomKindId?: string | null }) => {
    if (!a?.followUpKind) return ''
    return a.followUpKind === 'Custom' ? `Custom:${a.followUpCustomKindId ?? ''}` : a.followUpKind
  }

  const setFollowUp = (index: number, value: string) => {
    if (value.startsWith('Custom:')) {
      setValue(`answers.${index}.followUpKind`, 'Custom', { shouldDirty: true })
      setValue(`answers.${index}.followUpCustomKindId`, value.slice('Custom:'.length), { shouldDirty: true })
    } else {
      setValue(`answers.${index}.followUpKind`, (value || null) as FollowUpKind | null, { shouldDirty: true })
      setValue(`answers.${index}.followUpCustomKindId`, null, { shouldDirty: true })
    }
  }

  const createMutation = useCreateSurveyQuestion()
  const updateMutation = useUpdateSurveyQuestion(question?.id || '')

  useEffect(() => {
    if (!isOpen) return

    if (question) {
      reset({
        label: question.label,
        labelSpanish: question.labelSpanish || '',
        question: question.question,
        questionSpanish: question.questionSpanish || '',
        type: question.type,
        cycle: question.cycle,
        appliesTo: question.appliesTo,
        surveyGroupId: question.surveyGroupId || null,
        isActive: question.isActive,
        accessLevel: question.accessLevel,
        answers: question.answers.map((a, idx) => ({
          id: a.id,
          answerText: a.answerText,
          answerTextSpanish: a.answerTextSpanish || '',
          shortCode: a.shortCode || '',
          displayOrder: idx,
          followUpKind: a.followUpKind ?? null,
          followUpCustomKindId: a.followUpCustomKindId ?? null,
        })),
      })
    } else {
      reset({
        label: '',
        labelSpanish: '',
        question: '',
        questionSpanish: '',
        type: SurveyType.Volunteer,
        cycle: new Date().getFullYear(),
        appliesTo: AppliesTo.People,
        surveyGroupId: null,
        isActive: true,
        accessLevel: AccessLevel.AllCommittees,
        answers: [],
      })
    }
  }, [isOpen, question, reset])

  const onSubmit = async (data: SaveSurveyQuestionRequest) => {
    try {
      // Ensure display orders are sequential
      const dataWithOrders = {
        ...data,
        answers: data.answers.map((a, idx) => ({
          ...a,
          displayOrder: idx,
        })),
      }

      if (isEdit) {
        await updateMutation.mutateAsync(dataWithOrders)
        toaster.create({
          title: 'Question updated',
          description: 'The survey question has been updated successfully.',
          type: 'success',
          duration: 3000,
        })
      } else {
        await createMutation.mutateAsync(dataWithOrders)
        toaster.create({
          title: 'Question created',
          description: 'The survey question has been created successfully.',
          type: 'success',
          duration: 3000,
        })
      }
      onClose()
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: extractApiMessage(error, `Failed to ${isEdit ? 'update' : 'create'} question. Please try again.`),
        type: 'error',
        duration: 5000,
      })
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="90vh">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Dialog.Header>
                {isEdit ? 'Edit Survey Question' : 'Create Survey Question'}
                {question?.starterKey && <Badge ml={2} bg="accent.soft" color="accent">Starter</Badge>}
              </Dialog.Header>
              <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>

              <Dialog.Body maxH="calc(90vh - 140px)" overflowY="auto">
                <VStack gap={4} align="stretch">
                  <Field.Root required>
                    <Field.Label fontSize="sm">Label (Short Name)</Field.Label>
                    <Input size="sm" {...register('label', { required: true })} />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Label (Spanish)</Field.Label>
                    <Input size="sm" {...register('labelSpanish')} />
                  </Field.Root>

                  <Field.Root required>
                    <Field.Label fontSize="sm">Question Text</Field.Label>
                    <Textarea size="sm" rows={3} {...register('question', { required: true })} />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Question Text (Spanish)</Field.Label>
                    <Textarea size="sm" rows={3} {...register('questionSpanish')} />
                  </Field.Root>

                  <HStack gap={4}>
                    <Field.Root required>
                      <Field.Label fontSize="sm">Type</Field.Label>
                      <NativeSelect.Root size="sm">
                        <NativeSelect.Field {...register('type', { valueAsNumber: true, required: true })}>
                          {Object.entries(SURVEY_TYPE_LABELS).map(([value, label]) => (
                            <option key={value} value={parseInt(value)}>
                              {label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>

                    <Field.Root required>
                      <Field.Label fontSize="sm">Cycle Year</Field.Label>
                      <Input
                        size="sm"
                        type="number"
                        {...register('cycle', { valueAsNumber: true, required: true })}
                      />
                    </Field.Root>
                  </HStack>

                  <HStack gap={4}>
                    <Field.Root required>
                      <Field.Label fontSize="sm">Applies To</Field.Label>
                      <NativeSelect.Root size="sm">
                        <NativeSelect.Field
                          {...register('appliesTo', { valueAsNumber: true, required: true })}>
                          {Object.entries(APPLIES_TO_LABELS).map(([value, label]) => (
                            <option key={value} value={parseInt(value)}>
                              {label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>

                    <Field.Root required>
                      <Field.Label fontSize="sm">Access Level</Field.Label>
                      <NativeSelect.Root size="sm">
                        <NativeSelect.Field
                          {...register('accessLevel', { valueAsNumber: true, required: true })}>
                          {Object.entries(ACCESS_LEVEL_LABELS).map(([value, label]) => (
                            <option key={value} value={parseInt(value)}>
                              {label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  </HStack>

                  <Field.Root>
                    <Field.Label fontSize="sm">Survey Group (Optional)</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        placeholder="No group"
                        {...register('surveyGroupId', { setValueAs: v => v || null })}>
                        {groups?.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>

                  <Field.Root>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">Active</Text>
                      <Switch.Root
                        checked={isActive}
                        onCheckedChange={(e) => setValue('isActive', e.checked === true)}
                      >
                        <Switch.HiddenInput />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    </HStack>
                  </Field.Root>

                  <Separator />

                  <Box>
                    <HStack justify="space-between" mb={3}>
                      <Text fontSize="sm" fontWeight="bold">Answers</Text>
                      <Button
                        size="sm"
                        onClick={() => append({ answerText: '', answerTextSpanish: '', shortCode: '', displayOrder: fields.length, followUpKind: null, followUpCustomKindId: null })}
                        variant="ghost"
                        colorPalette="brand"><FiPlus />Add Answer
                                          </Button>
                    </HStack>

                    <VStack gap={3} align="stretch">
                      {fields.map((field, index) => (
                        <Box key={field.id} p={3} bg="gray.50" borderRadius="md">
                          <HStack align="start" gap={2}>
                            <Box pt={2}>
                              <FiMove size={16} color="gray" />
                            </Box>
                            <VStack flex={1} gap={2} align="stretch">
                              <Stack direction={{ base: 'column', md: 'row' }} gap={2}>
                                <Field.Root required flex={3}>
                                  <Input
                                    size="sm"
                                    placeholder="Answer text"
                                    {...register(`answers.${index}.answerText`, { required: true })}
                                  />
                                </Field.Root>
                                <Field.Root flex={2}>
                                  <Input
                                    size="sm"
                                    placeholder="Answer (Spanish)"
                                    {...register(`answers.${index}.answerTextSpanish`)}
                                  />
                                </Field.Root>
                                <Field.Root flex={1}>
                                  <Input
                                    size="sm"
                                    placeholder="Code"
                                    {...register(`answers.${index}.shortCode`)}
                                  />
                                </Field.Root>
                              </Stack>
                              {/* Deliberately NOT inside a Field.Root: Field.Root silently disables NativeSelect. */}
                              <NativeSelect.Root size="sm">
                                <NativeSelect.Field
                                  aria-label="Creates follow-up"
                                  value={followUpValue(watchedAnswers?.[index])}
                                  onChange={(e) => setFollowUp(index, e.target.value)}
                                >
                                  <option value="">Creates follow-up: None</option>
                                  <option value="SignDropOff">{FOLLOW_UP_KIND_LABELS.SignDropOff}</option>
                                  <option value="CallBack">{FOLLOW_UP_KIND_LABELS.CallBack}</option>
                                  <option value="Chase">{FOLLOW_UP_KIND_LABELS.Chase}</option>
                                  {activeCustomKinds.map(k => (
                                    <option key={k.id} value={`Custom:${k.id}`}>Custom: {k.name}</option>
                                  ))}
                                  {(() => {
                                    const a = watchedAnswers?.[index]
                                    const id = a?.followUpKind === 'Custom' ? a.followUpCustomKindId : null
                                    if (!id || activeCustomKinds.some(k => k.id === id)) return null
                                    const name = followUpKinds?.find(k => k.id === id)?.name
                                    return (
                                      <option value={`Custom:${id}`} disabled>
                                        {name ? `Custom: ${name} (inactive)` : 'Custom: (removed kind)'}
                                      </option>
                                    )
                                  })()}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            </VStack>
                            <IconButton
                              aria-label="Remove answer"
                              size="sm"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() => remove(index)}><FiTrash2 /></IconButton>
                          </HStack>
                        </Box>
                      ))}

                      {fields.length === 0 && (
                        <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                          No answers yet. Click "Add Answer" to create options.
                        </Text>
                      )}
                    </VStack>
                  </Box>
                </VStack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose} size="sm">
                  Cancel
                </Button>
                <Button
                  colorPalette="brand"
                  type="submit"
                  loading={isLoading}
                  size="sm"
                >
                  {isEdit ? 'Update' : 'Create'}
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
